import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const PDFPreview = ({ tripData, isGenerating }) => {
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    // Format trip data for preview display matching trip detail view layout
    if (tripData) {
      const formattedData = {
        title: tripData?.title || tripData?.destination || 'State Park Adventure',
        destination: tripData?.destination || tripData?.park,
        duration: tripData?.duration ? `${tripData?.duration} Days` : '3 Days',
        participants: tripData?.participants || tripData?.travelers || 2,
        estimatedCost: tripData?.estimatedCost || tripData?.total_cost || 'Moderate',
        transportation: tripData?.transportation || 'Personal Vehicle',
        accommodation: tripData?.accommodation || 'Camping',
        featuredParks: tripData?.featuredParks || [tripData?.destination || tripData?.park],
        activityTypes: tripData?.activityTypes || tripData?.activities || [],
        weatherForecast: tripData?.weatherForecast || 'Check local conditions',
        days: tripData?.days || [],
        accommodations: tripData?.accommodations,
        dining: tripData?.dining,
        reservationInfo: tripData?.reservationInfo
      };
      setPreviewData(formattedData);
    }
  }, [tripData]);

  if (isGenerating) {
    return (
      <div className="bg-card border border-border rounded-organic p-8">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="w-full h-96 bg-muted rounded-organic mb-4"></div>
            <div className="h-4 bg-muted rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="bg-card border border-border rounded-organic p-8 text-center">
        <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No trip data available for preview</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-organic overflow-hidden">
      {/* Preview Header */}
      <div className="bg-primary/10 px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-organic flex items-center justify-center">
              <Icon name="FileText" size={20} color="white" />
            </div>
            <div>
              <h3 className="font-heading font-heading-semibold text-foreground">PDF Preview</h3>
              <p className="text-sm text-muted-foreground">Matches your trip details page layout</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Eye" size={16} />
            <span>Live Preview</span>
          </div>
        </div>
      </div>

      {/* PDF Content Preview - Matching Trip Detail View Layout */}
      <div className="p-8 bg-white" style={{ maxHeight: '600px', overflowY: 'auto' }}>
        {/* Document Header */}
        <div className="mb-8 text-center border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-heading font-heading-bold text-gray-900 mb-3">
            {previewData?.title}
          </h1>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <span className="flex items-center space-x-1">
              <Icon name="MapPin" size={14} />
              <span>{previewData?.destination}</span>
            </span>
            <span className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>{previewData?.duration}</span>
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Generated on {new Date()?.toLocaleDateString()} by ParkTrip Planner AI
          </p>
        </div>

        {/* Trip Overview - Matching Detail View Stats */}
        <div className="mb-6">
          <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            Trip Overview
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Calendar" size={16} className="text-primary" />
                <p className="text-xs text-gray-600">Duration</p>
              </div>
              <p className="font-medium text-gray-900">{previewData?.duration}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Users" size={16} className="text-success" />
                <p className="text-xs text-gray-600">Travelers</p>
              </div>
              <p className="font-medium text-gray-900">{previewData?.participants}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Car" size={16} className="text-accent" />
                <p className="text-xs text-gray-600">Transportation</p>
              </div>
              <p className="font-medium text-gray-900">{previewData?.transportation}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Home" size={16} className="text-secondary" />
                <p className="text-xs text-gray-600">Accommodation</p>
              </div>
              <p className="font-medium text-gray-900">{previewData?.accommodation}</p>
            </div>
          </div>
        </div>

        {/* Featured Parks */}
        {previewData?.featuredParks?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
              Featured Parks
            </h2>
            <ul className="list-disc list-inside space-y-1">
              {previewData?.featuredParks?.map((park, index) => (
                <li key={index} className="text-gray-900">{park}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Activities */}
        {previewData?.activityTypes?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
              Planned Activities
            </h2>
            <div className="flex flex-wrap gap-2">
              {previewData?.activityTypes?.map((activity, index) => (
                <span key={index} className="bg-accent/10 text-accent text-xs px-3 py-1 rounded-full">
                  {activity}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Daily Itinerary - Matching Detail View */}
        {previewData?.days?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Daily Itinerary
            </h2>
            {previewData?.days?.map((day, dayIndex) => (
              <div key={dayIndex} className="mb-4 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                  <Icon name="Calendar" size={18} />
                  <span>Day {dayIndex + 1} - {day?.title || day?.date}</span>
                </h3>
                {day?.activities?.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {day?.activities?.map((activity, actIndex) => (
                      <div key={actIndex} className="flex items-start space-x-3 pl-4">
                        <Icon name="Clock" size={14} className="mt-1 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity?.time} - {activity?.title}</p>
                          {activity?.location && (
                            <p className="text-xs text-gray-600 flex items-center space-x-1 mt-1">
                              <Icon name="MapPin" size={12} />
                              <span>{activity?.location}</span>
                            </p>
                          )}
                          {activity?.description && (
                            <p className="text-xs text-gray-600 mt-1">{activity?.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Accommodation Details */}
        {previewData?.accommodations && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
              Accommodation Options
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {previewData?.accommodations?.campgrounds?.slice(0, 2)?.map((campground, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900 text-sm">{campground?.name}</p>
                  <p className="text-xs text-gray-600">{campground?.location}</p>
                  <p className="text-xs text-success mt-1">{campground?.cost}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dining Information */}
        {previewData?.dining && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
              Dining Options
            </h2>
            <div className="space-y-2">
              {previewData?.dining?.onSite?.slice(0, 3)?.map((option, index) => (
                <p key={index} className="text-sm text-gray-900">• {option}</p>
              ))}
            </div>
          </div>
        )}

        {/* Weather Forecast */}
        {previewData?.weatherForecast && (
          <div className="mb-6">
            <h2 className="text-xl font-heading font-heading-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">
              Weather Information
            </h2>
            <div className="bg-blue-50 p-3 rounded-lg flex items-center space-x-2">
              <Icon name="Sun" size={16} className="text-warning" />
              <p className="text-sm text-gray-900">{previewData?.weatherForecast}</p>
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
          <p className="font-medium mb-1">Generated by ParkTrip Planner AI</p>
          <p>Visit www.parktripplannerai.com for more amazing adventures</p>
          <p className="text-xs text-gray-500 mt-2">This document contains your complete trip itinerary and recommendations</p>
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;